import { useEffect } from "react";

/**
 * Per-route SEO hook. Runs client-side (this is a Vite SPA, no SSR), same
 * pattern already used by I18nProvider (src/i18n/useI18n.tsx) for the
 * language-driven title/description — this hook additionally lets each
 * *page* set its own title/description/canonical/OG/Twitter tags instead
 * of only the global i18n-driven ones, and supports marking auth-gated
 * pages as noindex.
 *
 * Google's crawler does execute JS and picks up tags set this way (Google
 * has documented this since ~2018 for its own bot), but other crawlers/
 * social-preview bots (e.g. link-unfurling bots for chat apps) often do
 * NOT execute JS — for those, the static tags in index.html (title, meta
 * description, og:*, twitter:*, JSON-LD) are what gets read on first
 * paint, and this hook's per-route overrides apply on top only for
 * clients that do run JS. See docs/seo-trust-audit.md for the full
 * rationale and the follow-up recommendation (SSR/prerendering) that
 * would close this gap completely.
 *
 * IMPORTANT — do not call this on the Home page ("/"): I18nProvider
 * (src/i18n/useI18n.tsx) already owns title/og:title/og:description for
 * Home specifically (t.meta.* is Home's own marketing hook copy, language-
 * aware via the language switcher). Since React fires child effects
 * before parent effects, calling useSEO from a page AND having
 * I18nProvider's effect fire in the same commit would let whichever runs
 * last silently win a title/description race. Splitting ownership by
 * route (Home = i18n-driven meta; every other route = this hook) avoids
 * that race entirely instead of relying on effect-ordering assumptions.
 */
export type SeoOptions = {
  title: string;
  description: string;
  /** Absolute canonical URL for this route, e.g. "https://jyotish-astro.com/agb". */
  canonical: string;
  /** Set true for account-gated pages (login, dashboards, admin) so they
   *  don't compete with public marketing pages in search results and so
   *  crawlers don't waste budget on pages they can't usefully index
   *  anyway (content differs per signed-in user). */
  noindex?: boolean;
};

function setMetaByName(name: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setMetaByProperty(property: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setRobots(noindex: boolean) {
  setMetaByName("robots", noindex ? "noindex, nofollow" : "index, follow");
}

/**
 * Sets the four language + x-default `<link rel="alternate" hreflang=...>`
 * tags for the current route.
 *
 * This is a single-domain client-side-i18n SPA: the same URL (e.g.
 * https://jyotish-astro.com/beratung) serves all four languages, switched
 * at runtime by the LanguageSwitcher (I18nProvider). Google's guidance for
 * that setup is that every language alternate points at the SAME canonical
 * URL — we are not (yet) using per-language path prefixes or subdomains, so
 * de/en/fr/es all resolve to `canonical`, and x-default points at the same
 * canonical (the default language is auto-detected client-side, defaulting
 * to German). This still tells search engines the page is available in
 * those four languages, which is what surfaces the language-appropriate
 * result in each locale's SERP / AI answer. If per-language routes are
 * introduced later, this is the single place to switch to distinct hrefs.
 *
 * Tags are keyed by hreflang and reused across route changes (updated in
 * place, never duplicated).
 */
const HREFLANGS = ["de", "en", "fr", "es", "x-default"] as const;
function setHreflang(canonical: string) {
  for (const lang of HREFLANGS) {
    let el = document.head.querySelector<HTMLLinkElement>(
      `link[rel="alternate"][hreflang="${lang}"]`
    );
    if (!el) {
      el = document.createElement("link");
      el.setAttribute("rel", "alternate");
      el.setAttribute("hreflang", lang);
      document.head.appendChild(el);
    }
    el.setAttribute("href", canonical);
  }
}

/**
 * Applies the given SEO fields to the document head on mount / whenever
 * they change. Intentionally does NOT touch <html lang> (I18nProvider
 * owns that) or clobber it — this only sets title/description/canonical/
 * OG/Twitter/robots, which are route-specific rather than language-specific
 * (I18nProvider re-applies the language-driven og:title/description
 * globally on language switch; this hook's route-specific call after that
 * effect still wins for the current page since effects run in mount/
 * dependency order after the provider's own effect on the same render).
 */
export function useSEO({ title, description, canonical, noindex = false }: SeoOptions) {
  useEffect(() => {
    document.title = title;
    setMetaByName("description", description);
    setCanonical(canonical);
    setMetaByProperty("og:title", title);
    setMetaByProperty("og:description", description);
    setMetaByProperty("og:url", canonical);
    setMetaByName("twitter:title", title);
    setMetaByName("twitter:description", description);
    setRobots(noindex);
    if (!noindex) setHreflang(canonical);
  }, [title, description, canonical, noindex]);
}

/**
 * Injects one or more additional JSON-LD `<script>` blocks into the page
 * head, keyed by `id` so re-renders replace rather than duplicate them, and
 * removes them on unmount so navigating away from the page doesn't leave
 * stale structured data describing content that's no longer shown.
 *
 * Unlike `useSEO()`, this is safe to call from the Home page too: it only
 * *adds* extra `<script type="application/ld+json">` tags and never
 * touches `document.title`/`meta[name=description]`/OG tags, so it can't
 * race with I18nProvider's title/description ownership of Home (see
 * useSEO's header comment for that specific race) — JSON-LD blocks from
 * different pages don't compete for the same DOM node the way two
 * `<title>` writers would.
 */
export function useJsonLd(id: string, data: object | object[]) {
  // Depend on the serialized string, not the object reference — callers
  // typically build `data` inline from render-scoped values (e.g. i18n
  // strings), which would otherwise get a new object identity every
  // render and needlessly remove+recreate the <script> tag each time.
  const isEmptyArray = Array.isArray(data) && data.length === 0;
  const serialized = JSON.stringify(data);
  useEffect(() => {
    const scriptId = `jsonld-${id}`;
    let el = document.head.querySelector<HTMLScriptElement>(`script#${scriptId}`);
    // Skip rendering an empty `[]` block (e.g. before async data has
    // loaded) — that's not meaningful structured data and would just be
    // noise for crawlers reading the page before hydration settles.
    if (isEmptyArray) {
      el?.remove();
      return;
    }
    if (!el) {
      el = document.createElement("script");
      el.id = scriptId;
      el.type = "application/ld+json";
      document.head.appendChild(el);
    }
    el.textContent = serialized;
    return () => {
      el?.remove();
    };
  }, [id, serialized, isEmptyArray]);
}
